import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { useAuthStore } from '@/features/auth';

interface Plugin {
  id: string;
  type: string;
  name: string;
  description?: string;
  enabled: boolean;
  config?: any;
  createdAt: string;
  updatedAt: string;
}

interface PluginContextType {
  plugins: Plugin[];
  loading: boolean;
  isPluginEnabled: (type: string) => boolean;
  togglePlugin: (type: string, enabled: boolean) => Promise<void>;
  refreshPlugins: () => Promise<void>;
}

const PluginContext = createContext<PluginContextType | undefined>(undefined);

interface PluginProviderProps {
  children: ReactNode;
}

export function PluginProvider({ children }: PluginProviderProps) {
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, accessToken } = useAuthStore();

  const fetchPlugins = async () => {
    // Only fetch if authenticated AND have valid token
    if (!isAuthenticated || !accessToken) {
      console.log('🔌 PluginContext: User not authenticated or no token, skipping fetch', {
        isAuthenticated,
        hasToken: !!accessToken
      });
      setPlugins([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔌 PluginContext: Fetching plugins from /api/v1/plugins');
      const { data } = await api.get('/plugins');
      console.log('🔌 PluginContext: Received data:', data);
      setPlugins(data.data || []);
      console.log('🔌 PluginContext: Plugins set to:', data.data || []);
    } catch (error) {
      console.error('❌ PluginContext: Failed to fetch plugins:', error);
      // Don't throw - allow app to continue even if plugins API fails
      setPlugins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlugins();
  }, [isAuthenticated, accessToken]); // Re-fetch when auth state or token changes

  const isPluginEnabled = (type: string): boolean => {
    const plugin = plugins.find((p) => p.type === type);
    return plugin?.enabled ?? false;
  };

  const togglePlugin = async (type: string, enabled: boolean) => {
    try {
      await api.patch(`/plugins/${type}/toggle`, { enabled });
      await fetchPlugins(); // Refresh plugin list
    } catch (error) {
      console.error('Failed to toggle plugin:', error);
      throw error;
    }
  };

  const value = {
    plugins,
    loading,
    isPluginEnabled,
    togglePlugin,
    refreshPlugins: fetchPlugins
  };

  return <PluginContext.Provider value={value}>{children}</PluginContext.Provider>;
}

export const usePlugins = () => {
  const context = useContext(PluginContext);
  if (!context) {
    throw new Error('usePlugins must be used within a PluginProvider');
  }
  return context;
};
